import React from 'react';
import classNames from 'classnames';

export const SEARCHING_STEPS: readonly string[] = ['+3 more', '+20 more', '+77 more', '+114 more'] as const;

interface SearchingPillProps {
  /** If true, immediately show final state (for completed cards) */
  immediate?: boolean;
  /** Abort signal to cancel the animation */
  signal?: AbortSignal;
  /** Called when the cycling animation completes */
  onComplete?: () => void;
}

export const SearchingPill: React.FC<SearchingPillProps> = (props) => {
  const { immediate = false, signal, onComplete } = props;

  const [text, setText] = React.useState<string>(
    immediate ? SEARCHING_STEPS[SEARCHING_STEPS.length - 1] : 'Searching...',
  );
  const [isFinal, setIsFinal] = React.useState<boolean>(immediate);
  const [isAnimating, setIsAnimating] = React.useState<boolean>(false);
  const mountedRef = React.useRef<boolean>(true);

  const triggerIconPop = React.useCallback(() => {
    setIsAnimating(false);
    // Force reflow via requestAnimationFrame
    requestAnimationFrame(() => {
      if (mountedRef.current) {
        setIsAnimating(true);
      }
    });
  }, []);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  React.useEffect(() => {
    if (immediate) return;

    let cancelled = false;

    const abortHandler = () => {
      cancelled = true;
    };
    signal?.addEventListener('abort', abortHandler);

    const run = async () => {
      for (let i = 0; i < SEARCHING_STEPS.length; i++) {
        await new Promise<void>((resolve) => setTimeout(resolve, 805));
        if (cancelled || !mountedRef.current) return;

        triggerIconPop();
        setText(SEARCHING_STEPS[i]);
      }

      if (!cancelled && mountedRef.current) {
        setIsFinal(true);
        await new Promise<void>((resolve) => setTimeout(resolve, 345));
        if (!cancelled && mountedRef.current) {
          onComplete?.();
        }
      }
    };

    run();

    return () => {
      cancelled = true;
      signal?.removeEventListener('abort', abortHandler);
    };
  }, [immediate, signal, triggerIconPop, onComplete]);

  return (
    <div className={'searching-pill'}>
      <div className={classNames('stacked-icons', { animating: isAnimating })}>
        {[0, 1, 2].map((idx) => (
          <div key={idx} className={'icon'} />
        ))}
      </div>
      <span className={classNames('text', { final: isFinal })}>{text}</span>
    </div>
  );
};
